import * as React from "react";
import { cn } from "@/lib/utils";
import { type LucideIcon, ServerCog } from "lucide-react";
import {
  COLOR_PRESETS,
  FONT_SIZE_PRESETS,
  type ColorPresetKey,
  type FontSizePresetKey,
  type TextStyle,
} from "@/types/sections";

export type SectionVariant = "left" | "center";

type SectionProps = {
  id?: string;
  eyebrow?: string;
  eyebrowStyle?: TextStyle;
  title: React.ReactNode;
  titleStyle?: TextStyle;
  subtitle?: React.ReactNode;
  subtitleStyle?: TextStyle;

  /** Leading Lucide icon */
  Icon?: LucideIcon;
  actions?: React.ReactNode;

  children?: React.ReactNode;

  /** Layout / appearance */
  variant?: SectionVariant;
  padded?: boolean;
  divider?: boolean;

  /** Centered underline separator */
  separatorColorClass?: string;
  separatorWidthClass?: string;
  separatorHeightClass?: string;

  /** Class overrides */
  className?: string;
  containerClassName?: string;
};

export default function Section({
  id,
  eyebrow,
  eyebrowStyle,
  title,
  titleStyle,
  subtitle,
  subtitleStyle,
  Icon = ServerCog,
  actions,
  children,

  variant = "left",
  padded = true,

  // defaults per your request
  separatorColorClass = "bg-[#FCD2C3]",
  separatorWidthClass = "w-16",
  separatorHeightClass = "h-[3px]",

  className,
  containerClassName,
}: SectionProps) {
  const isCenter = variant === "center";
  const eyebrowStyleProps = React.useMemo(
    () => buildTextStyleProps(eyebrowStyle, `${id ?? "section"}-eyebrow`),
    [eyebrowStyle, id]
  );
  const titleStyleProps = React.useMemo(
    () => buildTextStyleProps(titleStyle, `${id ?? "section"}-title`),
    [titleStyle, id]
  );
  const subtitleStyleProps = React.useMemo(
    () => buildTextStyleProps(subtitleStyle, `${id ?? "section"}-subtitle`),
    [subtitleStyle, id]
  );

  return (
    <section
      id={id}
      aria-labelledby={id ? `${id}-title` : undefined}
      className={cn("relative w-full scroll-mt-32", padded && "py-10 md:py-14", className)}
    >
      <div className={cn("mx-auto max-w-6xl px-6 md:px-10 lg:px-16", containerClassName)}>
        <div
          className={cn(
            "flex items-start gap-6",
            isCenter ? "flex-col items-center text-center" : "justify-between"
          )}
        >
          <div className={cn("flex items-start gap-4", isCenter && "flex-col items-center")}>
            {!isCenter && (
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#FCD2C3] bg-[#FCD2C3] backdrop-blur-sm">
                <Icon className="h-5 w-5 opacity-80 text-black/80" aria-hidden="true" />
              </div>
            )}

            <div className="flex-1">
              {eyebrow && (
                <p
                  className={cn(
                    "mb-2 text-sm font-medium uppercase tracking-[0.14em] text-black/50 dark:text-[#FCD2C3]",
                    "flex items-center gap-2",
                    isCenter ? "justify-center" : "justify-start",
                    eyebrowStyleProps.className
                  )}
                  style={eyebrowStyleProps.style}
                >
                  {isCenter && <Icon className="h-4 w-4" aria-hidden="true" />}
                  {eyebrow}
                </p>
              )}

              <h2
                id={id ? `${id}-title` : undefined}
                className={cn(
                  "font-semibold tracking-tight",
                  isCenter ? "text-3xl md:text-4xl text-center" : "text-2xl md:text-3xl text-left",
                  titleStyleProps.className
                )}
                style={titleStyleProps.style}
              >
                {title}
              </h2>

              {isCenter && (
                <div
                  className={cn(
                    "mx-auto mt-2 mb-4 rounded-full",
                    separatorColorClass,
                    separatorWidthClass,
                    separatorHeightClass
                  )}
                />
              )}

              {subtitle && (
                <p
                  className={cn(
                    "mt-2 text-muted-foreground leading-relaxed",
                    isCenter
                      ? "mx-auto max-w-2xl text-sm md:text-base text-center"
                      : "max-w-2xl text-sm md:text-base text-left",
                    subtitleStyleProps.className
                  )}
                  style={subtitleStyleProps.style}
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {!isCenter && actions && <div className="shrink-0">{actions}</div>}
        </div>

        {children && <div className="mt-6 md:mt-8">{children}</div>}
      </div>
    </section>
  );
}

const getColorValue = (key?: ColorPresetKey) =>
  COLOR_PRESETS.find((preset) => preset.key === key)?.value;

const getSizeValue = (key?: FontSizePresetKey) =>
  FONT_SIZE_PRESETS.find((preset) => preset.key === key)?.value;

export const buildTextStyleProps = (style?: TextStyle, prefix = "section") => {
  const css: (React.CSSProperties & Record<string, string>) = {};
  const classes: string[] = [];
  const color = getColorValue(style?.color);
  if (color) css.color = color;
  const baseSize = getSizeValue(style?.size?.base);
  if (baseSize) css.fontSize = baseSize;
  const mdSize = getSizeValue(style?.size?.md);
  if (mdSize) {
    const varName = `--${prefix}-md-size`;
    css[varName] = mdSize;
    classes.push(`md:text-[var(${varName})]`);
  }
  return {
    style: Object.keys(css).length ? (css as React.CSSProperties) : undefined,
    className: classes.join(" "),
  };
};
